import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ImportDeviceModelInput } from '@grosync/common';
import { importDeviceModels } from '../../api/deviceModels';

/** zustand persist 키 — 구 deviceModelStore가 localStorage에 저장하던 사용자 모델 */
const LS_KEY = 'grosync-device-models';

interface PersistedModel {
  id: string;
  name: string;
  code: string;
  deviceType: string;
  description?: string;
  tagline?: string;
  sensors: ImportDeviceModelInput['sensors'];
  actuators?: ImportDeviceModelInput['actuators'];
  growthEnabled: boolean;
  growthFields: string[];
  comm?: string;
  battery?: string;
  interval?: string;
  firmware?: string;
}

function readLocalModels(): PersistedModel[] {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { state?: { models?: PersistedModel[] } };
    const models = parsed?.state?.models;
    return Array.isArray(models) ? models : [];
  } catch {
    return [];
  }
}

function toImportInput(m: PersistedModel): ImportDeviceModelInput {
  return {
    name: m.name,
    code: m.code,
    deviceType: m.deviceType as ImportDeviceModelInput['deviceType'],
    description: m.description,
    tagline: m.tagline,
    sensors: m.sensors ?? [],
    actuators: m.actuators ?? [],
    growthEnabled: m.growthEnabled,
    growthFields: m.growthFields ?? [],
    comm: m.comm,
    battery: m.battery,
    interval: m.interval,
    firmware: m.firmware,
    oldId: m.id,
  };
}

/**
 * 구 localStorage 사용자 모델을 서버로 1회 임포트한다.
 * - 성공해야만 localStorage 키를 비운다(데이터 손실 방지).
 * - 임포트할 모델이 없으면 키만 정리한다.
 * @returns 서버에 임포트/remap이 일어났으면 true (쿼리 무효화 필요)
 */
async function importLocalDeviceModelsOnce(): Promise<boolean> {
  const models = readLocalModels();
  if (models.length === 0) {
    localStorage.removeItem(LS_KEY);
    return false;
  }
  await importDeviceModels({ models: models.map(toImportInput) });
  localStorage.removeItem(LS_KEY); // 임포트 성공 후에만 비운다
  return true;
}

/**
 * 앱 진입 시(장비모델 화면 마운트) 1회 실행되는 localStorage → 서버 마이그레이션 훅.
 * 실패 시 localStorage를 보존해 다음 기회에 재시도한다.
 */
export function useLocalDeviceModelMigration(): void {
  const qc = useQueryClient();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!localStorage.getItem(LS_KEY)) return;

    importLocalDeviceModelsOnce()
      .then((changed) => {
        if (changed) void qc.invalidateQueries({ queryKey: ['device-models'] });
      })
      .catch((err) => {
        console.warn('[device-models] localStorage 임포트 실패 — 다음 로드에서 재시도:', err);
      });
  }, [qc]);
}
